import { useDesktopUpdates } from './desktop-updates';
import { cn } from '@/components/ui/cn';

/** A small on/off switch matching the Settings page style. */
function Switch({ on, onToggle, label }: { on: boolean; onToggle: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onToggle(!on)}
      className={cn('relative h-6 w-11 shrink-0 rounded-full transition-colors', on ? 'bg-accent' : 'bg-layer-strong')}
    >
      <span className={cn('absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform', on ? 'left-[22px]' : 'left-0.5')} />
    </button>
  );
}

/**
 * Desktop app updates — self-hosted OTA. Renders nothing outside the desktop shell. `variant="section"`
 * is a full Settings card; `variant="menu"` is a compact block for the account flyout.
 */
export default function DesktopUpdatePanel({ variant = 'section' }: { variant?: 'section' | 'menu' }) {
  const { isDesktop, version, auto, state, setAuto, check } = useDesktopUpdates();
  if (!isDesktop) return null;

  const checkLabel = state === 'checking' ? 'Checking…' : state === 'latest' ? 'Up to date' : 'Check for updates';

  if (variant === 'menu') {
    return (
      <div className="px-2 py-1.5">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span className="text-[12px] font-semibold text-fg-soft">Desktop app{version ? ` · v${version}` : ''}</span>
          <button
            type="button"
            onClick={check}
            disabled={state === 'checking'}
            className="rounded-md px-2 py-1 text-[12px] font-medium text-accent transition-colors hover:bg-layer disabled:opacity-60"
          >
            {checkLabel}
          </button>
        </div>
        <label className="flex items-center justify-between gap-2 text-[12.5px] text-fg-soft">
          <span>Update automatically</span>
          <Switch on={auto} onToggle={setAuto} label="Update automatically" />
        </label>
      </div>
    );
  }

  return (
    <section className="mb-6 rounded-2xl border border-line bg-surface-raised p-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-[14px] font-semibold text-fg-strong">Desktop app{version ? ` · v${version}` : ''}</div>
          <p className="mt-0.5 text-[12.5px] text-fg-faint">
            Updates install themselves in the background and apply on the next launch. New features arrive
            without re-downloading the app.
          </p>
        </div>
        <button
          type="button"
          onClick={check}
          disabled={state === 'checking'}
          className="shrink-0 rounded-lg border border-line bg-layer px-3 py-1.5 text-[12.5px] font-semibold text-fg-soft transition-colors hover:bg-layer-strong hover:text-fg disabled:opacity-60"
        >
          {checkLabel}
        </button>
      </div>
      <div className="mt-4 flex items-center justify-between gap-4 border-t border-line pt-4">
        <div>
          <div className="text-[13px] font-medium text-fg-strong">Update automatically</div>
          <p className="mt-0.5 text-[12px] text-fg-faint">Install new versions on launch (recommended).</p>
        </div>
        <Switch on={auto} onToggle={setAuto} label="Update automatically" />
      </div>
    </section>
  );
}
